import type { ISessionRequest, IUserRequest } from '@/data/_autogen/IValveVista';
import { TokenType, UserRequestType } from '@/data/_autogen/IValveVista';

/**
 * Mock Auth Data
 * Simulates backend auth responses for development
 */

// Mock registered users database
export const MOCK_USERS: IUserRequest[] = [
  {
    userId: 'user-001',
    userName: 'johndoe',
    firstName: 'John',
    lastName: 'Doe',
    emailAddress: 'john@example.com',
    password: 'password123', // For demo only - never do this in real app
  },
  {
    userId: 'user-002',
    userName: 'janedoe',
    firstName: 'Jane',
    lastName: 'Doe',
    emailAddress: 'jane@example.com',
    password: 'password123',
  },
];

/**
 * Generate a mock JWT token (realistic format, not cryptographically valid)
 */
export const generateMockToken = (
  type: 'access' | 'refresh' | 'guest',
  userId?: string
): string => {
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = type === 'guest' ? 3600 : 86400; // 1 hour for guest, 24 hours for user

  const payload = btoa(
    JSON.stringify({
      sub: userId || 'guest',
      type,
      iat: now,
      exp: now + expiresIn,
      iss: 'valve-vista-api',
      aud: 'valve-vista-app',
    })
  );

  const signature = btoa('mock_signature_' + Math.random().toString(36));
  return `${header}.${payload}.${signature}`;
};

/**
 * Mock guest token response
 */
export const generateMockGuestTokenResponse = (): ISessionRequest => {
  const token = generateMockToken('guest');
  return {
    isGuest: true,
    token,
    headerName: 'X-Guest-Token',
    expiresInSeconds: 3600, // 1 hour
    tokenType: TokenType.GuestToken,
    userRequest: {
      userRequestType: UserRequestType.GuestSession,
    },
  };
};

/**
 * Mock login response
 */
export const generateMockLoginResponse = (userName: string): ISessionRequest | null => {
  const user = MOCK_USERS.find(
    (u) => u.userName === userName || u.emailAddress === userName
  );

  if (!user) {
    return null;
  }

  const accessToken = generateMockToken('access', user.userId);
  return {
    isGuest: false,
    token: accessToken,
    headerName: 'Authorization',
    expiresInSeconds: 86400, // 24 hours
    tokenType: TokenType.AccessToken,
    userRequest: {
      userId: user.userId,
      userName: user.userName,
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.emailAddress,
      userRequestType: UserRequestType.UserLogin,
    },
  };
};

/**
 * Mock registration response
 */
export const generateMockRegistrationResponse = (
  userRequest: IUserRequest
): ISessionRequest => {
  // In real app, would validate and create user
  // For mock, just create a new user ID and return session
  const userId = `user-${Math.floor(Math.random() * 1000000)}`;

  const accessToken = generateMockToken('access', userId);
  return {
    isGuest: false,
    token: accessToken,
    headerName: 'Authorization',
    expiresInSeconds: 86400, // 24 hours
    tokenType: TokenType.AccessToken,
    userRequest: {
      userId,
      userName: userRequest.userName,
      firstName: userRequest.firstName,
      lastName: userRequest.lastName,
      emailAddress: userRequest.emailAddress,
      userRequestType: UserRequestType.UserRegistration,
    },
  };
};

/**
 * Mock session response (me endpoint)
 */
export const generateMockSessionResponse = (
  token: string,
  isGuest: boolean
): ISessionRequest => {
  if (isGuest) {
    return {
      isGuest: true,
      token,
      headerName: 'X-Guest-Token',
      expiresInSeconds: 3600,
      tokenType: TokenType.GuestToken,
      userRequest: {
        userRequestType: UserRequestType.GuestSession,
      },
    };
  }

  // For registered user, extract info from token (in real app, would validate server-side)
  const user = MOCK_USERS[Math.floor(Math.random() * MOCK_USERS.length)];

  return {
    isGuest: false,
    token,
    headerName: 'Authorization',
    expiresInSeconds: 86400,
    tokenType: TokenType.AccessToken,
    userRequest: {
      userId: user.userId,
      userName: user.userName,
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.emailAddress,
    },
  };
};

/**
 * Simulate network delay in mock responses
 */
export const delayResponse = (ms = 800): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 */
export const isValidPassword = (password: string): boolean => {
  // At least 8 chars, 1 uppercase, 1 lowercase, 1 number
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password)
  );
};

/**
 * Check if email already registered
 */
export const isEmailRegistered = (email: string): boolean => {
  return MOCK_USERS.some((u) => u.emailAddress === email);
};
